// src/components/admin/Newsletter.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  Mail,
  Send,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  AlertCircle,
  X,
  Users,
  Calendar,
  FileText,
  Paperclip,
  AlertTriangle,
  Eye,
  Copy,
  Plus,
  Globe,
  History,
  Inbox,
  Clock,
  Check
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios"
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { showToast } from '../../utils/toast';

// Function to format date from database
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Function to check if date is within last 30 days
const isWithinLast30Days = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return date >= thirtyDaysAgo;
};

const Newsletter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Check if user has required editorial level
    const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);
  
    if (!isAuthorized) {
      return (
        <Unauthorized
          message="You need editor-in-chief or admin privileges to access this feature."
        />
      );
  }
  const quillRef = useRef(null);
  const viewQuillRef = useRef(null);
  const quillInstance = useRef(null);
  const viewQuillInstance = useRef(null);

  // State for UI
  const [activeTab, setActiveTab] = useState('subscribers'); // 'subscribers' or 'history'
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showViewEmailModal, setShowViewEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sending, setSending] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // State for email form
  const [emailForm, setEmailForm] = useState({
    subject: '',
    recipients: 'all' // 'all', 'selected'
  });

  // Data states
  const [mailingList, setMailingList] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [statsData, setStatsData] = useState({
    totalSubscribers: 0,
    recentSubscribers: 0,
    totalSent: 0,
    successful: 0,
    failed: 0,
    totalRecipients: 0,
    domains: []
  });
  const [loading, setLoading] = useState(false);

  // Fetch functions
  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/newsletter/subscribers');
      if (response.data.status === 'success') {
        // Format the data for display
        const formattedList = response.data.emailList.map(item => ({
          ...item,
          date: formatDate(item.date_joined)
        }));
        setMailingList(formattedList);
      } else if (response.data.error === 'NoEmailListFound') {
        setMailingList([]);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      showToast('Failed to load subscribers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const response = await axios.get('/api/newsletter/history');
      if (response.data.status === 'success') {
        // Format the history data
        const formattedHistory = response.data.history.map(item => ({
          ...item,
          sentDate: formatDate(item.sent_at),
          recipients: item.recipient_count,
          // Add mock data for opens and clicks (you'll need to track these separately)
          opened: Math.floor(Math.random() * item.recipient_count),
          clicked: Math.floor(Math.random() * item.recipient_count * 0.7)
        }));
        setEmailHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/newsletter/stats');
      if (response.data.status === 'success') {
        setStatsData(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchSubscribers();
    fetchEmailHistory();
    fetchStats();
  }, []);

  // Stats calculation using data from API
  const stats = {
    total: statsData.totalSubscribers,
    uniqueDomains: statsData.domains?.length || 0,
    recent: statsData.recentSubscribers,
    totalSent: statsData.totalSent,
    totalRecipients: statsData.totalRecipients
  };

  // Filter mailing list based on search
  const filteredList = mailingList.filter(item =>
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.id?.toString().includes(searchTerm)
  );

  // Filter email history based on search
  const filteredHistory = emailHistory.filter(item =>
    item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination for subscribers
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredList.slice(startIndex, endIndex);

  // Pagination for history
  const [historyPage, setHistoryPage] = useState(1);
  const historyTotalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const historyStartIndex = (historyPage - 1) * itemsPerPage;
  const historyEndIndex = historyStartIndex + itemsPerPage;
  const currentHistory = filteredHistory.slice(historyStartIndex, historyEndIndex);

  // Update selectAll when current items change
  useEffect(() => {
    if (selectAll) {
      setSelectedEmails(currentItems.map(item => item.id));
    }
  }, [currentItems, selectAll]);

  // Initialize Quill editor for compose
  useEffect(() => {
    if (!quillRef.current || !showComposeModal) return;

    if (quillInstance.current) return;

    quillInstance.current = new Quill(quillRef.current, {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'script': 'sub' }, { 'script': 'super' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'direction': 'rtl' }],
          [{ 'size': ['small', false, 'large', 'huge'] }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'font': [] }],
          [{ 'align': [] }],
          ['clean'],
          ['link', 'image', 'video']
        ],
      },
      theme: 'snow',
      placeholder: 'Compose your newsletter...',
    });

    const initialContent = [
      { insert: 'Dear Subscriber,\n\n' },
      { insert: 'Welcome to the ASFIRJ Newsletter. Here are the latest updates from our journal:\n\n' },
      { insert: '• Latest publications\n' },
      { insert: '• Upcoming events\n' },
      { insert: '• Call for papers\n\n' },
      { insert: 'Best regards,\nThe ASFIRJ Team\n' }
    ];
    quillInstance.current.setContents(initialContent);
  }, [showComposeModal]);

  // Initialize Quill editor for view modal
  useEffect(() => {
    if (!viewQuillRef.current || !showViewEmailModal || !selectedEmail) return;

    if (viewQuillInstance.current) {
      viewQuillInstance.current.setContents(selectedEmail.content);
      return;
    }

    viewQuillInstance.current = new Quill(viewQuillRef.current, {
      modules: {
        toolbar: false
      },
      theme: 'snow',
      readOnly: true
    });

    viewQuillInstance.current.setContents(selectedEmail.content);
  }, [showViewEmailModal, selectedEmail]);

  const handleSelectEmail = (id) => {
    setSelectedEmails(prev => {
      const newSelection = prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id];

      setSelectAll(newSelection.length === currentItems.length && currentItems.length > 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmails([]);
      setSelectAll(false);
    } else {
      setSelectedEmails(currentItems.map(item => item.id));
      setSelectAll(true);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectAll(false);
    }
  };

  const handleHistoryPageChange = (page) => {
    if (page >= 1 && page <= historyTotalPages) {
      setHistoryPage(page);
    }
  };

  const handleComposeNew = () => {
    setShowComposeModal(true);
    setEmailForm({
      subject: '',
      recipients: 'all'
    });

    setTimeout(() => {
      if (quillInstance.current) {
        const initialContent = [
          { insert: 'Dear Subscriber,\n\n' },
          { insert: 'Welcome to the ASFIRJ Newsletter. Here are the latest updates from our journal:\n\n' },
          { insert: '• Latest publications\n' },
          { insert: '• Upcoming events\n' },
          { insert: '• Call for papers\n\n' },
          { insert: 'Best regards,\nThe ASFIRJ Team\n' }
        ];
        quillInstance.current.setContents(initialContent);
      }
    }, 100);
  };

  const handleCancelCompose = () => {
    setShowComposeModal(false);
    setEmailForm({
      subject: '',
      recipients: 'all'
    });
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setShowViewEmailModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewEmailModal(false);
    setSelectedEmail(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!emailForm.subject) {
      showToast('Please enter a subject', 'error');
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSend = async () => {
    setSending(true);
    setShowConfirmModal(false);

    try {
      const editorContent = quillInstance.current.getContents();

      const payload = {
        subject: emailForm.subject,
        content: editorContent,
        recipientType: emailForm.recipients,
        selectedIds: selectedEmails
      };

      const response = await axios.post('/api/newsletter/send', payload);

      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');

        // Refresh data
        fetchEmailHistory();
        fetchStats();

        setTimeout(() => {
          setShowComposeModal(false);
          setEmailForm({
            subject: '',
            recipients: 'all'
          });
          setSelectedEmails([]);
          setSelectAll(false);
        }, 3000);
      } else {
        throw new Error(response.data.error);
      }
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to send newsletter', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleExportCSV = () => {
    const csvContent = "ID,Email,Date\n" +
      mailingList.map(item => `${item.id},${item.email},${item.date}`).join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setHistoryPage(1);
    setSearchTerm('');
    setSelectedEmails([]);
    setSelectAll(false);
  };

  const handleDeleteSubscriber = async (id) => {
    if (window.confirm('Are you sure you want to delete this subscriber?')) {
      try {
        const response = await axios.delete(`/api/newsletter/subscriber/${id}`);
        if (response.data.status === 'success') {
          fetchSubscribers();
          fetchStats();
          showToast('Subscriber deleted successfully', 'success');
        }
      } catch (error) {
        showToast('Failed to delete subscriber', 'error');
      }
    }
  };

  return (
    <div className="container-fluid px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <Mail className="w-6 h-6 mr-3" />
          Newsletter Management
        </h1>
        <p className="text-gray-600 mt-1">Manage subscribers, send newsletters, and track email history</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Subscribers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-[--asfi-purple] bg-opacity-10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Unique Domains</p>
              <p className="text-2xl font-bold text-blue-600">{stats.uniqueDomains}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">New (30 days)</p>
              <p className="text-2xl font-bold text-green-600">{stats.recent}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sent</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalSent}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Recipients</p>
              <p className="text-2xl font-bold text-orange-600">{stats.totalRecipients}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Inbox className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px">
          <button
            onClick={() => handleTabChange('subscribers')}
            className={`py-4 px-6 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'subscribers'
                ? 'border-[--asfi-purple] text-[--asfi-purple]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <Users className="w-4 h-4" />
            Subscribers
            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {mailingList.length}
            </span>
          </button>

          <button
            onClick={() => handleTabChange('history')}
            className={`py-4 px-6 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'history'
                ? 'border-[--asfi-purple] text-[--asfi-purple]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <History className="w-4 h-4" />
            Email History
            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {emailHistory.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            {activeTab === 'subscribers' && (
              <>
                <span className="text-sm text-gray-600">
                  Total: <span className="font-medium">{filteredList.length}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">
                  Showing: <span className="font-medium">{startIndex + 1}-{Math.min(endIndex, filteredList.length)}</span>
                </span>
              </>
            )}
            {activeTab === 'history' && (
              <>
                <span className="text-sm text-gray-600">
                  Total: <span className="font-medium">{filteredHistory.length}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">
                  Showing: <span className="font-medium">{historyStartIndex + 1}-{Math.min(historyEndIndex, filteredHistory.length)}</span>
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeTab === 'subscribers' && (
              <button
                onClick={handleExportCSV}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            )}

            <button
              onClick={handleComposeNew}
              className="px-3 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              New Email
            </button>

            <button
              onClick={() => {
                fetchSubscribers();
                fetchEmailHistory();
                fetchStats();
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={activeTab === 'subscribers' ? "Search by email or ID..." : "Search by subject..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (activeTab === 'subscribers') {
                setCurrentPage(1);
                setSelectAll(false);
              } else {
                setHistoryPage(1);
              }
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
          />
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'subscribers' ? (
        /* Mailing List Table */
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[--asfi-purple]">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-[--asfi-purple] focus:ring-[--asfi-purple]"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Subscribed Date
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[--asfi-purple]"></div>
                      </div>
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedEmails.includes(item.id)}
                          onChange={() => handleSelectEmail(item.id)}
                          className="rounded border-gray-300 text-[--asfi-purple] focus:ring-[--asfi-purple]"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDeleteSubscriber(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No subscribers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Email History Table */
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[--asfi-purple]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Sent Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Opens
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentHistory.length > 0 ? (
                  currentHistory.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.sentDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.recipients}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.opened || 0} ({Math.round(((item.opened || 0) / item.recipients) * 100)}%)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.clicked || 0} ({Math.round(((item.clicked || 0) / item.recipients) * 100)}%)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${item.status == "sent"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                          }`}>
                          {item.status == "sent" ? (
                            <Check className="w-3 h-3 mr-1" />
                          ) : (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          )}
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewEmail(item)}
                            className="p-2 text-gray-600 hover:text-[--asfi-purple] hover:bg-[--asfi-purple] hover:bg-opacity-10 rounded-lg transition-colors"
                            title="View Email Content"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Resend"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No email history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* History Pagination */}
          {historyTotalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {historyPage} of {historyTotalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleHistoryPageChange(historyPage - 1)}
                  disabled={historyPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleHistoryPageChange(historyPage + 1)}
                  disabled={historyPage === historyTotalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compose Email Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                  Compose Newsletter
                </h3>
                <button
                  onClick={handleCancelCompose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Recipient Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Recipient</h4>

                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="recipients"
                          value="all"
                          checked={emailForm.recipients === 'all'}
                          onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">All Subscribers ({mailingList.length})</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="recipients"
                          value="selected"
                          checked={emailForm.recipients === 'selected'}
                          onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Selected ({selectedEmails.length})</span>
                      </label>
                    </div>

                    {emailForm.recipients === 'selected' && selectedEmails.length === 0 && (
                      <p className="text-sm text-orange-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        No emails selected. Please select recipients from the list above.
                      </p>
                    )}
                  </div>
                </div>

                {/* Subject Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Subject</h4>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
                    name="subject"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    placeholder="Enter newsletter subject"
                    required
                  />
                </div>

                {/* Message Body Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Message Body</h4>

                  {/* Quill Editor */}
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div ref={quillRef} style={{ height: '400px' }} />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCancelCompose}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sending || (emailForm.recipients === 'selected' && selectedEmails.length === 0)}
                    className="px-6 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {sending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Newsletter
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Email Modal */}
      {showViewEmailModal && selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-[--asfi-purple]" />
                  Email Content
                </h3>
                <button
                  onClick={handleCloseViewModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Subject:</p>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmail.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Sent Date:</p>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmail.sentDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Recipients:</p>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmail.recipients}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Status:</p>
                     <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${selectedItem.status == "sent"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                          }`}>
                          {selectedItem.status == "sent" ? (
                            <Check className="w-3 h-3 mr-1" />
                          ) : (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          )}
                          {selectedItem.status}
                        </span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message Content</h4>
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <div ref={viewQuillRef} style={{ height: '400px' }} />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseViewModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  className="px-4 py-2 bg-[--asfi-purple] text-white rounded-lg hover:bg-[--asfi-blue] transition-colors flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Resend
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Send</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to send this newsletter?
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Subject:</p>
                <p className="text-sm text-gray-900 mt-1">{emailForm.subject}</p>

                <p className="text-sm font-medium text-gray-700 mt-3">Recipients:</p>
                <p className="text-sm text-gray-900 mt-1">
                  {emailForm.recipients === 'all'
                    ? `All subscribers (${mailingList.length})`
                    : `${selectedEmails.length} selected subscribers`}
                </p>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> This will send an email to all selected recipients. Please review your content carefully.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  disabled={sending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default Newsletter;